import { getWuyouApiBase, getPlatformPreset } from "@/utils/apiPlatform";
import md5 from "md5";
export const WUYOU_NOTICE_CATEGORY_PLATFORM = "1710185991083995137";
/** 甲方 / 网关要求：请求 api.shengqingqy.cn 时 Token 与 Authorization 必须同时存在，缺一不可 */
export function assertWuyouApiAuth(apiToken, bearerAuthorization) {
    const token = String(apiToken || "").trim();
    if (!token)
        return { ok: false, msg: "\u7F3A\u5C11 Token" };
    const authNorm = normalizeBearerAuthorization(bearerAuthorization);
    if (!authNorm)
        return { ok: false, msg: "\u7F3A\u5C11\u6216\u65E0\u6548 Authorization\uff08\u987B\u540C\u65F6\u643A\u5E26\u975E\u7A7A Bearer JWT\uff09" };
    return { ok: true, authNorm };
}
function normalizeBearerAuthorization(bearerAuthorization) {
    const raw = String(bearerAuthorization || "").trim();
    if (!raw)
        return "";
    const withBearer = /^Bearer\s+/i.test(raw) ? raw : `Bearer ${raw}`;
    const jwt = withBearer.replace(/^Bearer\s+/i, "").trim();
    if (!jwt)
        return "";
    return `Bearer ${jwt}`;
}
/** 官方 H5 浏览器里可见的客户端 appid（与 query 中 sign_appid 一致，非密钥） */
export const WUYOU_H5_DEFAULT_SIGN_APPID = "h5_23514861";
function makeWuyouNonce(len = 16) {
    const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let s = "";
    for (let i = 0; i < len; i++)
        s += chars[Math.floor(Math.random() * chars.length)];
    return s;
}
function parseWuyouSignSnapshotJson(raw) {
    if (raw == null || String(raw).trim() === "")
        return null;
    try {
        const o = JSON.parse(String(raw));
        return o && typeof o === "object" ? o : null;
    }
    catch {
        return null;
    }
}
function mergeSignObjectIntoBase(base, snap) {
    if (!snap || typeof snap !== "object")
        return;
    for (const k of ["sign_appid", "timestamp", "nonce", "sign"]) {
        if (snap[k] != null && snap[k] !== "")
            base[k] = snap[k];
    }
}
/**
 * 将浏览器里复制的「含 sign 的完整 URL」或 `?pageNum=1&...&sign=xxx` 写入 localStorage，供 mergeWuyouSignQuery 使用。
 * 注意：签名与业务 query 绑定，列表翻页后需重新从 H5 抓包或配置 VITE_WUYOU_SIGN_MD5_SECRET 自动算签。
 */
export function persistWuyouSignSnapshotFromUrlOrQuery(input) {
    const raw = String(input || "").trim();
    if (!raw)
        return { ok: false, msg: "empty" };
    let search = "";
    try {
        if (/^https?:\/\//i.test(raw)) {
            const u = new URL(raw);
            search = u.search.startsWith("?") ? u.search.slice(1) : u.search;
        }
        else
            search = raw.startsWith("?") ? raw.slice(1) : raw;
    }
    catch {
        return { ok: false, msg: "bad url" };
    }
    const p = new URLSearchParams(search);
    const o = {};
    for (const k of ["sign_appid", "timestamp", "nonce", "sign"]) {
        const v = p.get(k);
        if (v != null && String(v).trim() !== "")
            o[k] = String(v).trim();
    }
    if (!o.sign_appid || !o.timestamp || !o.nonce || !o.sign)
        return { ok: false, msg: "missing sign quartet in query" };
    try {
        localStorage.setItem("wuyou_sign_snapshot", JSON.stringify(o));
    }
    catch {
        return { ok: false, msg: "storage failed" };
    }
    return { ok: true };
}
function readViteWuyouSignSnapshot() {
    try {
        const e = typeof import.meta !== "undefined" && import.meta.env ? import.meta.env : {};
        const raw = String(e.VITE_WUYOU_SIGN_SNAPSHOT || "").trim();
        return parseWuyouSignSnapshotJson(raw);
    }
    catch {
        return null;
    }
}
function readViteWuyouSignMd5Secret() {
    try {
        const e = typeof import.meta !== "undefined" && import.meta.env ? import.meta.env : {};
        return String(e.VITE_WUYOU_SIGN_MD5_SECRET || "").trim();
    }
    catch {
        return "";
    }
}
function readViteWuyouSignMode() {
    try {
        const e = typeof import.meta !== "undefined" && import.meta.env ? import.meta.env : {};
        return String(e.VITE_WUYOU_SIGN_MODE || "sorted_kv_key_suffix").trim();
    }
    catch {
        return "sorted_kv_key_suffix";
    }
}
/** 与官方 GET 一致：业务 query 须带 sign_appid、timestamp、nonce、sign（见 mergeWuyouSignQuery） */
function tryApplyWuyouMd5SignFromMergedQuery(base) {
    const secret = readViteWuyouSignMd5Secret();
    if (!secret)
        return;
    const ts = String(Math.floor(Date.now() / 1000));
    const nonce = makeWuyouNonce(16);
    base.timestamp = ts;
    base.nonce = nonce;
    if (!base.sign_appid || String(base.sign_appid).trim() === "")
        base.sign_appid = (typeof import.meta !== "undefined" && import.meta.env && String(import.meta.env.VITE_WUYOU_SIGN_APPID || "").trim()) || WUYOU_H5_DEFAULT_SIGN_APPID;
    const signPayload = { ...base };
    delete signPayload.sign;
    const keys = Object.keys(signPayload).sort();
    const str = keys.map((k) => `${k}=${signPayload[k]}`).join("&");
    const mode = readViteWuyouSignMode();
    if (mode === "md5_secret_prefix")
        base.sign = md5(`${secret}${str}`);
    else
        base.sign = md5(`${str}&key=${secret}`);
}
export function mergeWuyouSignQuery(query) {
    const base = { ...(query || {}) };
    try {
        mergeSignObjectIntoBase(base, parseWuyouSignSnapshotJson(localStorage.getItem("wuyou_sign_snapshot")));
    }
    catch {
    }
    try {
        mergeSignObjectIntoBase(base, parseWuyouSignSnapshotJson(sessionStorage.getItem("wuyou_sign_snapshot")));
    }
    catch {
    }
    try {
        mergeSignObjectIntoBase(base, readViteWuyouSignSnapshot());
    }
    catch {
    }
    try {
        const e = typeof import.meta !== "undefined" && import.meta.env ? import.meta.env : {};
        for (const [ek, qk] of [
            ["VITE_WUYOU_SIGN_APPID", "sign_appid"],
            ["VITE_WUYOU_SIGN_TIMESTAMP", "timestamp"],
            ["VITE_WUYOU_SIGN_NONCE", "nonce"],
            ["VITE_WUYOU_SIGN_VALUE", "sign"]
        ]) {
            const v = String(e[ek] || "").trim();
            if (v)
                base[qk] = v;
        }
    }
    catch {
    }
    if (!base.sign_appid || String(base.sign_appid).trim() === "")
        base.sign_appid = (typeof import.meta !== "undefined" && import.meta.env && String(import.meta.env.VITE_WUYOU_SIGN_APPID || "").trim()) || WUYOU_H5_DEFAULT_SIGN_APPID;
    if (!base.sign || String(base.sign).trim() === "")
        tryApplyWuyouMd5SignFromMergedQuery(base);
    return base;
}
function joinUrl(path) {
    const b = String(getWuyouApiBase() || "").replace(/\/$/, "");
    const p = String(path || "").startsWith("/") ? path : `/${path}`;
    return b + p;
}
/**
 * 官方 H5：POST /auth/app/password/login（无需 Token/Authorization；JSON 体合并 sign_*，密码为明文与抓包一致）
 */
export async function wuyouPasswordLogin({
    username,
    password,
    areaCode = "+86",
    inviteId = "",
    deviceType = 0,
    captchaTicket = "",
    captchaRandstr = ""
} = {}) {
    const u = String(username ?? "").trim();
    if (!u)
        return { code: 0, msg: "missing username" };
    const url = joinUrl("/auth/app/password/login");
    const body = mergeWuyouSignQuery({
        username: /^\d+$/.test(u) ? Number(u) : u,
        password: String(password ?? ""),
        areaCode: String(areaCode || "+86"),
        inviteId: inviteId != null ? String(inviteId) : "",
        deviceType: Number(deviceType) || 0,
        captchaTicket: captchaTicket != null ? String(captchaTicket) : "",
        captchaRandstr: captchaRandstr != null ? String(captchaRandstr) : ""
    });
    const preset = getPlatformPreset();
    const headers = {
        Accept: "application/json, text/plain, */*",
        "Accept-Language": "zh-CN,zh;q=0.9",
        "Content-Language": "zh-CN",
        "Content-Type": "application/json",
        Origin: "https://m.shengqingqy.cn",
        Referer: preset.ystHtmlReferer || "https://m.shengqingqy.cn/",
        "User-Agent": "Mozilla/5.0 (iPhone; CPU iPhone OS 16_6 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/16.6 Mobile/15E148 Safari/604.1"
    };
    const response = await fetch(url, { method: "POST", headers, body: JSON.stringify(body) });
    let resData = await response.json().catch(() => ({}));
    if (typeof resData === "string") {
        try {
            resData = JSON.parse(resData);
        }
        catch {
            resData = {};
        }
    }
    return resData && typeof resData === "object" ? resData : { code: 0, msg: "invalid json" };
}
/** 官方登录 data.token 常为 JWT（三段）；请求头 Token 多为短串（如 32 位 hex），与 Authorization Bearer JWT 分离 */
function isWuyouJwtShapedString(s) {
    const t = String(s ?? "").trim();
    if (!t || t.split(".").length !== 3)
        return false;
    return /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(t);
}
/**
 * 从密码登录响应中解析：
 * - **token**：写入请求头 `Token:`（短会话串；官方常见为 `ticket` / `apiToken` 等；勿把 JWT 误作 Token）
 * - **authorization**：`Authorization: Bearer <JWT>`（优先显式字段，否则用 JWT 形态的 `data.token`）
 */
export function extractWuyouPasswordLoginCredentials(res) {
    if (!res || typeof res !== "object")
        return { token: "", authorization: "" };
    const candidates = [res.data, unwrapWuyouPayload(res), res].filter((x) => x && typeof x === "object" && !Array.isArray(x));
    let explicitAuth = "";
    let jwt = "";
    let opaqueForHeader = "";
    for (const d of candidates) {
        if (!explicitAuth) {
            const a = d.authorization ?? d.Authorization ?? d.authToken ?? d.bearerToken;
            if (a != null && String(a).trim() !== "")
                explicitAuth = String(a).trim();
        }
        const pickOpaque = (v) => {
            if (v == null)
                return;
            const s = String(v).trim();
            if (!s || isWuyouJwtShapedString(s))
                return;
            if (!opaqueForHeader)
                opaqueForHeader = s;
        };
        pickOpaque(d.apiToken);
        pickOpaque(d.sessionToken);
        pickOpaque(d.sessionId);
        pickOpaque(d.sid);
        pickOpaque(d.memberSessionId);
        pickOpaque(d.ticket);
        const primary = d.token ?? d.accessToken ?? d.memberToken ?? d.userToken ?? d.loginToken;
        if (primary != null && String(primary).trim() !== "") {
            const ts = String(primary).trim();
            if (isWuyouJwtShapedString(ts)) {
                if (!jwt)
                    jwt = ts;
            }
            else if (!opaqueForHeader) {
                opaqueForHeader = ts;
            }
        }
    }
    let authorization = explicitAuth;
    if (!authorization && jwt)
        authorization = `Bearer ${jwt}`;
    if (authorization && !/^Bearer\s+/i.test(authorization))
        authorization = `Bearer ${authorization}`;
    let token = opaqueForHeader;
    if (!token && jwt)
        token = jwt;
    return { token, authorization };
}
/** 从密码登录包体中尽量取出「我的」展示用字段（含官方 `data.memberInfoVo`） */
export function extractWuyouPasswordLoginProfile(res) {
    let userId = "";
    let userName = "";
    let walletAddress = "";
    let headImage = "";
    if (!res || typeof res !== "object")
        return { userId, userName, walletAddress, headImage };
    const roots = [res.data, unwrapWuyouPayload(res), res].filter((x) => x && typeof x === "object" && !Array.isArray(x));
    const candidates = [];
    for (const d of roots) {
        candidates.push(d);
        const m = d.memberInfoVo;
        if (m && typeof m === "object" && !Array.isArray(m))
            candidates.push(m);
    }
    for (const d of candidates) {
        if (!userId) {
            const v = d.id ?? d.memberId ?? d.userId ?? d.uid ?? d.mid;
            if (v != null && String(v).trim() !== "")
                userId = String(v).trim();
        }
        if (!userName) {
            const v = d.nickName ?? d.nickname ?? d.name ?? d.username ?? d.realName ?? d.userPhone ?? d.mobile ?? d.phone;
            if (v != null && String(v).trim() !== "")
                userName = String(v).trim();
        }
        if (!walletAddress) {
            const v = d.walletAddress ?? d.blockchainAddress ?? d.chainAddress ?? d.wallet ?? d.address;
            if (v != null && String(v).trim() !== "")
                walletAddress = String(v).trim();
        }
        if (!headImage) {
            const v = d.avatar ?? d.headImage ?? d.headPic ?? d.headUrl;
            if (v != null && String(v).trim() !== "")
                headImage = String(v).trim();
        }
    }
    return { userId, userName, walletAddress, headImage };
}
/** 无忧 Token 常为 JWT：解析 payload 供「我的」兜底展示（失败返回 null） */
export function tryParseWuyouJwtPayload(apiToken) {
    const raw = String(apiToken || "").trim();
    if (!raw)
        return null;
    const parts = raw.split(".");
    if (parts.length < 2)
        return null;
    try {
        let b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
        const pad = b64.length % 4;
        if (pad)
            b64 += "=".repeat(4 - pad);
        const json = typeof atob === "function" ? atob(b64) : "";
        if (!json)
            return null;
        const o = JSON.parse(json);
        return o && typeof o === "object" && !Array.isArray(o) ? o : null;
    }
    catch {
        return null;
    }
}
/**
 * 从 GET /market/app/market/getHead/:productId 的 data 中取市场参考最低价（字段名随网关变化）
 * @returns 正数最低价（元），取不到则为 NaN
 */
export function pickWuyouMarketFloorPriceYuanFromHeadPayload(d) {
    if (!d || typeof d !== "object")
        return NaN;
    const pick = (v) => {
        const n = Number(v);
        return Number.isFinite(n) && n > 0 ? n : NaN;
    };
    const keys = [
        "minResellPrice",
        "minPrice",
        "floorPrice",
        "lowestPrice",
        "bottomPrice",
        "minSellPrice",
        "marketMinPrice",
        "marketFloor",
        "floor",
        "minListingPrice",
        "listingMinPrice"
    ];
    for (const k of keys) {
        const n = pick(d[k]);
        if (!Number.isNaN(n))
            return n;
    }
    const rows = d.rows ?? d.list ?? d.marketList ?? d.records;
    if (Array.isArray(rows) && rows.length) {
        let min = Infinity;
        for (const r of rows) {
            if (!r || typeof r !== "object")
                continue;
            const n = pick(r.price ?? r.sellPrice ?? r.amount ?? r.dealPrice ?? r.listingPrice);
            if (!Number.isNaN(n))
                min = Math.min(min, n);
        }
        if (Number.isFinite(min) && min > 0 && min !== Infinity)
            return min;
    }
    return NaN;
}
export function wuyouAuthHeaders(apiToken, bearerAuthorization, { jsonBody = false } = {}) {
    const preset = getPlatformPreset();
    const token = String(apiToken || "").trim();
    const authorization = normalizeBearerAuthorization(bearerAuthorization);
    const h = {
        Accept: "application/json, text/plain, */*",
        "Accept-Language": "zh-CN,zh;q=0.9",
        "Content-Language": "zh-CN",
        Token: token,
        Authorization: authorization,
        Origin: "https://m.shengqingqy.cn",
        Referer: preset.ystHtmlReferer || "https://m.shengqingqy.cn/",
        "User-Agent": "Mozilla/5.0 (iPhone; CPU iPhone OS 16_6 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/16.6 Mobile/15E148 Safari/604.1"
    };
    if (jsonBody)
        h["Content-Type"] = "application/json";
    return h;
}
export async function wuyouRequestWithAuth(method, path, apiToken, bearerAuthorization, { query = null, json = null } = {}) {
    const gate = assertWuyouApiAuth(apiToken, bearerAuthorization);
    if (!gate.ok)
        return { code: 0, msg: `\u65E0\u5FE7 API \u987B\u540C\u65F6\u643A\u5E26 Token \u4E0E Authorization\uff1a${gate.msg}` };
    let url = joinUrl(path);
    if (method === "GET" && query && typeof query === "object") {
        const merged = mergeWuyouSignQuery(query);
        const params = new URLSearchParams();
        Object.keys(merged).forEach((key) => {
            const v = merged[key];
            if (v !== null && v !== undefined)
                params.append(key, v === "" ? "" : String(v));
        });
        const qs = params.toString();
        if (qs)
            url += (url.includes("?") ? "&" : "?") + qs;
    }
    const headers = wuyouAuthHeaders(apiToken, gate.authNorm, { jsonBody: json != null && String(method).toUpperCase() !== "GET" });
    const config = { method, headers };
    if (json != null && method !== "GET")
        config.body = JSON.stringify(json);
    const response = await fetch(url, config);
    let resData = await response.json().catch(() => ({}));
    if (typeof resData === "string") {
        try {
            resData = JSON.parse(resData);
        }
        catch {
        }
    }
    return resData;
}
const memberCategoriesInflight = new Map();
export async function wuyouMemberCategories(token, auth) {
    const k = `${String(token || "").trim()}\u001e${String(auth || "").trim()}`;
    const hit = memberCategoriesInflight.get(k);
    if (hit)
        return hit;
    const p = wuyouRequestWithAuth("GET", "/product/app/category/getMemberCategories", token, auth, { query: {} }).finally(() => {
        if (memberCategoriesInflight.get(k) === p)
            memberCategoriesInflight.delete(k);
    });
    memberCategoriesInflight.set(k, p);
    return p;
}
/**
 * 判断 /market/app/market/list 是否成功：网关可能返回 { code:200, rows }，也可能直接 { total, rows }（与 scan_013 离线探测一致）。
 */
export function wuyouMarketListResponseOk(res) {
    if (!res || typeof res !== "object")
        return false;
    const code = Number(res.code);
    if (Number.isFinite(code) && code > 0 && code !== 200)
        return false;
    if (code === 200)
        return true;
    return Array.isArray(res.rows);
}
/** 与 market/list 相同包体：member/nft/list 等 */
export function wuyouMemberNftListResponseOk(res) {
    return wuyouMarketListResponseOk(res);
}
/** 分类 getMemberCategories：须为 data 数组（可无顶层 code，与网关一致） */
export function wuyouMemberCategoriesResponseOk(res) {
    if (!res || typeof res !== "object")
        return false;
    const code = Number(res.code);
    if (Number.isFinite(code) && code > 0 && code !== 200)
        return false;
    return Array.isArray(res.data);
}
/** 订单列表分页，与 market 相同 rows/total 包体 */
export function wuyouOrderListResponseOk(res) {
    return wuyouMarketListResponseOk(res);
}
/** 公告列表（含 rows + statistical） */
export function wuyouNoticeListResponseOk(res) {
    if (!res || typeof res !== "object")
        return false;
    const code = Number(res.code);
    if (Number.isFinite(code) && code > 0 && code !== 200)
        return false;
    if (code === 200)
        return true;
    return Array.isArray(res.rows);
}
export function wuyouProductDetailResponseOk(res) {
    if (!res || typeof res !== "object")
        return false;
    const code = Number(res.code);
    if (Number.isFinite(code) && code > 0 && code !== 200)
        return false;
    if (code === 200)
        return true;
    const p = unwrapWuyouPayload(res);
    if (!p || typeof p !== "object")
        return false;
    return !!(p.productId ?? p.id ?? p.name ?? p.productName ?? p.image ?? p.nftName);
}
export function wuyouMemberNftDetailResponseOk(res) {
    if (!res || typeof res !== "object")
        return false;
    const code = Number(res.code);
    if (Number.isFinite(code) && code > 0 && code !== 200)
        return false;
    if (code === 200)
        return true;
    const p = unwrapWuyouPayload(res);
    if (!p || typeof p !== "object")
        return false;
    return !!(p.nftId ?? p.memberNftId ?? p.id ?? p.nftName ?? p.picture);
}
/** GET /market/app/market/info/:id — 单笔挂单详情，常见 { code:200, data } */
export function wuyouMarketInfoResponseOk(res) {
    if (!res || typeof res !== "object")
        return false;
    const code = Number(res.code);
    if (Number.isFinite(code) && code > 0 && code !== 200)
        return false;
    if (code === 200)
        return true;
    const p = unwrapWuyouPayload(res);
    if (!p || typeof p !== "object")
        return false;
    return !!(p.id ?? p.productId ?? p.name ?? p.price ?? p.image);
}
/** 订单详情 order/info/:id */
export function wuyouOrderInfoPayload(res) {
    if (!res || typeof res !== "object")
        return null;
    const code = Number(res.code);
    if (Number.isFinite(code) && code > 0 && code !== 200)
        return null;
    if (code === 200 && res.data != null && typeof res.data === "object")
        return res.data;
    const p = unwrapWuyouPayload(res);
    if (p && typeof p === "object" && (p.orderId != null || p.id != null || p.orderSn != null))
        return p;
    return null;
}
/** GET /order/app/order/preOrder 成功：常见 { code:200, data:{...} } 或直接 data */
export function wuyouPreOrderResponseOk(res) {
    if (!res || typeof res !== "object")
        return false;
    const code = Number(res.code);
    if (code === 401 || code === 403)
        return false;
    if (Number.isFinite(code) && code > 0 && code !== 200)
        return false;
    const topMsg = String(res.msg || res.message || "").trim();
    if (code === 200 && topMsg && (topMsg.includes("\u5931\u8D25") || topMsg.includes("\u9519\u8BEF") || topMsg.includes("\u8D85\u8FC7") || topMsg.includes("\u8BA4\u8BC1")))
        return false;
    if (code === 200)
        return true;
    const p = unwrapWuyouPayload(res);
    if (!p || typeof p !== "object")
        return false;
    const pMsg = String(p.msg || p.message || "").trim();
    if (pMsg && (pMsg.includes("\u5931\u8D25") || pMsg.includes("\u9519\u8BEF") || pMsg.includes("\u8D85\u8FC7")))
        return false;
    return !!(p.payAmount != null || p.amount != null || p.orderAmount != null || p.price != null || p.orderId != null || p.payUrl != null || p.orderSn != null);
}
export function unwrapWuyouPreOrderData(res) {
    if (!res || typeof res !== "object")
        return null;
    if (Number(res.code) === 200 && res.data != null)
        return res.data;
    const p = unwrapWuyouPayload(res);
    return p && typeof p === "object" ? p : null;
}
/** POST submit 成功 */
export function wuyouOrderSubmitResponseOk(res) {
    if (!res || typeof res !== "object")
        return false;
    const code = Number(res.code);
    if (code === 200 || code === 1)
        return true;
    return res.success === true || res.orderId != null || (res.data && (res.data.orderId != null));
}
/** 与官网 / scan_013 一致：用 GET /market/app/market/list 轻量校验 Token+Authorization（含 mergeWuyouSignQuery） */
export async function checkWuyouSession(apiToken, bearerAuthorization) {
    const raw = await wuyouMarketResaleList(apiToken, bearerAuthorization, { pageNum: 1, pageSize: 1 });
    if (wuyouMarketListResponseOk(raw))
        return { code: 200, msg: "ok" };
    return raw && typeof raw === "object" ? raw : { code: 0, msg: "invalid response" };
}
export async function wuyouMemberNftList(apiToken, bearerAuthorization, pageNum = 1, pageSize = 100, extraQuery = {}) {
    return wuyouRequestWithAuth("GET", "/product/app/member/nft/list", apiToken, bearerAuthorization, {
        query: { pageNum, pageSize, ...extraQuery }
    });
}
/** 藏品（商品）维度详情：路径参数为 productId */
export async function wuyouProductDetail(apiToken, bearerAuthorization, productId) {
    const pid = String(productId || "").trim();
    if (!pid)
        return { code: 0, msg: "missing productId" };
    const enc = encodeURIComponent(pid);
    return wuyouRequestWithAuth("GET", `/product/app/product/detail/${enc}`, apiToken, bearerAuthorization, { query: {} });
}
/** 用户持有单件 NFT 详情：query id 为 member/nft 主键 */
export async function wuyouMemberNftGetDetail(apiToken, bearerAuthorization, nftRowId) {
    const id = String(nftRowId || "").trim();
    if (!id)
        return { code: 0, msg: "missing id" };
    return wuyouRequestWithAuth("GET", "/product/app/member/nft/getDetail", apiToken, bearerAuthorization, { query: { id } });
}
export function unwrapWuyouPayload(res) {
    if (!res || typeof res !== "object")
        return null;
    const d = res.data;
    if (d != null && typeof d === "object" && !Array.isArray(d))
        return d;
    return res;
}
/**
 * 路由进入会员藏品详情时，GET getDetail 的 query **id** 与官网一致多来自 **gId**；
 * 少数入口误写为 **qld**；亦兼容 **memberNftId**。
 */
export function pickWuyouMemberNftDetailRouteId(routeQuery) {
    if (!routeQuery || typeof routeQuery !== "object")
        return "";
    const one = (v) => {
        if (v == null)
            return "";
        if (Array.isArray(v))
            return v[0] != null ? String(v[0]) : "";
        return String(v);
    };
    const g = one(routeQuery.gId).trim();
    if (g)
        return g;
    const qld = one(routeQuery.qld).trim();
    if (qld)
        return qld;
    return one(routeQuery.memberNftId).trim();
}
export function adaptWuyouProductDetailToDetailInfo(raw, routeProductId, { fallbackPrice = "" } = {}) {
    const row = raw && typeof raw === "object" ? raw : {};
    const name = row.name ?? row.productName ?? row.title ?? "";
    const img = row.image ?? row.img ?? row.coverUrl ?? row.infoImage ?? row.thumb ?? row.pic ?? "";
    const issued = row.issuedCount ?? row.issueNum ?? row.issueCount ?? row.limitNum ?? row.sellLimit;
    const circ = row.memberProductCount ?? row.marketCount ?? row.circulation ?? row.count ?? row.soldCount ?? row.soldNum;
    const price = row.price ?? row.minPrice ?? row.floorPrice ?? fallbackPrice;
    const aid = String(row.productId ?? row.id ?? routeProductId ?? "").trim() || String(routeProductId ?? "").trim();
    const issuedN = issued != null && issued !== "" ? Number(issued) : NaN;
    const circN = circ != null && circ !== "" ? Number(circ) : NaN;
    return {
        ...row,
        albumId: aid,
        albumName: name,
        gName: name,
        thumbPic: img,
        gUrls: img ? [{ url: img }] : [],
        sellLimit: Number.isFinite(issuedN) ? issuedN : issued ?? "",
        issueNum: issued,
        soldNum: Number.isFinite(circN) ? circN : Number(circ) || 0,
        priceCny: price,
        price,
        authorName: row.authorName ?? row.author ?? "",
        authorHeadImage: row.authorImage ?? row.authorHeadImage ?? row.authorAvatar ?? "",
        tokenId: row.hash ?? row.tokenId ?? row.blockchainHash ?? "",
        gId: aid,
        gNum: "",
        star: row.star ?? row.collectCount ?? row.focusNum ?? 0,
        wuyouProductOnly: true
    };
}
export function adaptWuyouMemberNftDetailToDetailInfo(raw, routeProductId) {
    const row = raw && typeof raw === "object" ? raw : {};
    /** 与官方一致：data.hasOpenMarket 为 true 时允许「我要寄售」等入口（由详情页读取 info.hasOpenMarket） */
    const nftId = row.nftId ?? row.memberNftId ?? row.id;
    const productId = row.productId ?? routeProductId;
    const num = row.number ?? row.num ?? row.serialNum ?? row.goodsNum ?? row.gNum;
    const name = row.nftName ?? row.name ?? "";
    const img = row.picture ?? row.image ?? row.img ?? "";
    const price = row.price ?? row.sellPrice ?? row.resellPrice ?? row.amount ?? "";
    const pid = String(productId ?? routeProductId ?? "").trim();
    return {
        ...row,
        albumId: pid,
        albumName: name,
        gName: name,
        thumbPic: img,
        gUrls: img ? [{ url: img }] : [],
        gId: nftId != null ? String(nftId) : "",
        gNum: num != null && num !== "" ? String(num) : "",
        sellLimit: row.issueNum ?? row.limitNum ?? row.totalNum ?? row.sellLimit ?? "",
        soldNum: row.circulation ?? row.soldNum ?? 0,
        priceCny: price,
        price,
        authorName: row.authorName ?? "",
        authorHeadImage: row.authorImage ?? row.authorHeadImage ?? "",
        ownerName: row.ownerName ?? row.holderName ?? "",
        ownerAvatar: row.ownerAvatar ?? row.holderAvatar ?? "",
        tokenId: row.hash ?? row.tokenId ?? "",
        resell: row.resell ?? row.onResell,
        onSale: row.onSale,
        isBuyer: row.isBuyer,
        wuyouProductOnly: false
    };
}
/**
 * 挂单详情 market/info → ProductDetail 展示结构。
 * gId 与列表 mapWuyouMarketRowToOnSaleItem 一致：优先 memberNftId / nftId / goodsId，再 objectId，供 preOrder 兜底参数与列表购买对齐。
 */
export function adaptWuyouMarketInfoToDetailInfo(raw, routeProductId) {
    const row = raw && typeof raw === "object" ? raw : {};
    const pid = String(row.productId ?? routeProductId ?? "").trim();
    const nftId = row.nftId ?? row.memberNftId ?? row.goodsId;
    const objectId = row.objectId != null && String(row.objectId).trim() !== "" ? String(row.objectId).trim() : "";
    const gidFromNft = nftId != null && String(nftId).trim() !== "" ? String(nftId).trim() : "";
    const gIdResolved = gidFromNft || objectId || (row.memberNftId != null && String(row.memberNftId).trim() !== "" ? String(row.memberNftId).trim() : "");
    const name = String(row.name ?? row.productName ?? "").trim();
    const img = row.image ?? row.infoImage ?? row.img ?? "";
    const price = row.price ?? row.sellPrice ?? "";
    const tags = Array.isArray(row.tags) ? row.tags.map((t) => String(t)) : [];
    const no = row.numberObj && typeof row.numberObj === "object"
        ? `${String(row.numberObj.number ?? "").trim()}${String(row.numberObj.suffix ?? "").trim()}`
        : "";
    const numberLine = String(no || row.number || "").trim();
    return {
        ...row,
        wuyouMarketListingMode: true,
        albumId: pid,
        albumName: name,
        gName: name,
        thumbPic: img,
        gUrls: img ? [{ url: img }] : [],
        priceCny: price,
        price,
        gId: gIdResolved,
        gNum: "",
        authorName: row.authorName ?? "",
        authorHeadImage: row.authorImage ?? row.authorHeadImage ?? "",
        sellLimit: row.issuedCount ?? row.issueNum ?? "",
        soldNum: row.memberProductCount ?? row.count ?? "",
        star: 0,
        tokenId: row.hash ?? row.tokenId ?? "",
        wuyouMarketTags: tags,
        wuyouMarketContentHtml: row.content != null ? String(row.content) : "",
        wuyouMarketAuthorDetailed: row.authorDetailed != null ? String(row.authorDetailed) : "",
        wuyouMarketPurchaseNotes: row.purchaseNotes != null ? String(row.purchaseNotes) : "",
        wuyouNumberDisplay: numberLine,
        wuyouIssuerName: row.issuerName != null ? String(row.issuerName) : "",
        wuyouCategoryName: row.categoryName != null ? String(row.categoryName) : ""
    };
}
/**
 * 将 market/list、market/userList、market/record/list 等单行映射为 OnSaleList 卡片结构。
 */
export function mapWuyouMarketRowToOnSaleItem(r, { tagPreset = null } = {}) {
    const row = r && typeof r === "object" ? r : {};
    const nftId = row.nftId ?? row.memberNftId ?? row.goodsId;
    const objectId = row.objectId ?? row.memberNftId;
    const num = row.number ?? row.num ?? row.serialNum ?? row.goodsNum;
    const price = row.price ?? row.sellPrice ?? row.amount ?? row.resellPrice ?? row.dealPrice;
    const gid = nftId != null && String(nftId).trim() !== "" ? String(nftId).trim() : objectId != null && String(objectId).trim() !== "" ? String(objectId).trim() : "";
    const listingIdRaw = row.marketId ?? row.listingId ?? row.marketListingId ?? row.id;
    const listingId = listingIdRaw != null && String(listingIdRaw).trim() !== "" ? String(listingIdRaw).trim() : "";
    const numberObj = row.numberObj && typeof row.numberObj === "object" ? row.numberObj : null;
    const numShort = numberObj
        ? `${String(numberObj.number ?? "").trim()}${String(numberObj.suffix ?? "").trim()}`
        : "";
    const numberDisplay = String(numShort || num || row.number || "").trim();
    let tagList;
    if (Array.isArray(tagPreset) && tagPreset.length) {
        tagList = tagPreset.map((x) => String(x));
    }
    else {
        tagList = ["\u5BC4\u552E"];
        const extraTag = row.categoryName != null && String(row.categoryName).trim() !== ""
            ? String(row.categoryName).trim()
            : Array.isArray(row.tags) && row.tags.length ? String(row.tags[0]).trim() : "";
        if (extraTag)
            tagList.push(extraTag);
    }
    return {
        ...row,
        wuyouMarketListingId: listingId,
        wuyouRowTags: tagList,
        wuyouNumberDisplay: numberDisplay,
        gId: gid,
        gNum: num != null && num !== "" ? String(num) : "",
        goodsNumber: numberDisplay,
        gName: row.nftName ?? row.name ?? row.productName ?? "",
        priceCny: price,
        price,
        resell: Number(row.resell ?? 0)
    };
}
function adaptWuyouRowsToOnSalePagination(res, { pageNum = 1, pageSize = 20, mapRow } = {}) {
    if (!wuyouMarketListResponseOk(res))
        return { code: 0, data: { list: [] } };
    const rows = Array.isArray(res.rows) ? res.rows : [];
    const list = typeof mapRow === "function" ? rows.map((r, i) => mapRow(r, i)) : rows;
    const total = Number(res.total);
    const loaded = (Number(pageNum) - 1) * Number(pageSize) + rows.length;
    const hasNext = Number.isFinite(total) && total > 0 ? loaded < total : rows.length >= Number(pageSize);
    return { code: 1, data: { list, hashNext: hasNext ? 1 : 0, hasNext: hasNext ? 1 : 0 } };
}
/** 将某藏品的寄售列表转为 LST 在售列表形态，供 OnSaleList 复用 */
export function adaptWuyouMarketListingByProductToOnSaleLst(res, { pageNum = 1, pageSize = 20 } = {}) {
    return adaptWuyouRowsToOnSalePagination(res, {
        pageNum,
        pageSize,
        mapRow: (r) => mapWuyouMarketRowToOnSaleItem(r)
    });
}
/** 用户侧某藏品寄售（寄售中） */
export function adaptWuyouMarketUserListToOnSaleLst(res, { pageNum = 1, pageSize = 20 } = {}) {
    return adaptWuyouRowsToOnSalePagination(res, {
        pageNum,
        pageSize,
        mapRow: (r) => mapWuyouMarketRowToOnSaleItem(r, { tagPreset: ["\u5BC4\u552E\u4E2D"] })
    });
}
/** 成交记录列表 */
export function adaptWuyouMarketRecordListToOnSaleLst(res, { pageNum = 1, pageSize = 20 } = {}) {
    return adaptWuyouRowsToOnSalePagination(res, {
        pageNum,
        pageSize,
        mapRow: (r) => mapWuyouMarketRowToOnSaleItem(r, { tagPreset: ["\u5DF2\u6210\u4EA4"] })
    });
}
export async function wuyouMarketResaleList(token, auth, { pageNum = 1, pageSize = 20, type = "1", sortType = "1", key = "", screen = "", categoryId = "", getType = "", status = "", productId = "" } = {}) {
    const query = {
        pageNum,
        pageSize,
        type: String(type),
        key: String(key ?? ""),
        screen: String(screen ?? ""),
        sortType: String(sortType),
        getType: String(getType ?? ""),
        status: String(status ?? "")
    };
    const c = String(categoryId || "").trim();
    if (c)
        query.categoryId = c;
    const pid = String(productId || "").trim();
    if (pid)
        query.productId = pid;
    return wuyouRequestWithAuth("GET", "/market/app/market/list", token, auth, { query });
}
/**
 * 某藏品的寄售/挂单列表：与官方 H5 一致需带 productId；sortType 在首页市场为 0/1/2…，单品列表沿用 4 表示「按该藏品」，
 * 价格升降序时改为与 Holdings 一致的 1（低→高）、2（高→低）；按编号排序时仍传 4，由前端对当前已加载列表做二次排序。
 */
export async function wuyouMarketListingByProduct(token, auth, { productId, pageNum = 1, pageSize = 20, sortType = "4" } = {}) {
    const pid = String(productId || "").trim();
    if (!pid)
        return { code: 0, msg: "missing productId", rows: [], total: 0 };
    return wuyouMarketResaleList(token, auth, {
        pageNum,
        pageSize,
        type: "1",
        sortType: String(sortType ?? "4"),
        key: "",
        screen: "",
        getType: "",
        categoryId: "",
        status: "",
        productId: pid
    });
}
/** 求购列表分页包体与 market/list 类似 */
export function wuyouWantBuyListResponseOk(res) {
    return wuyouMarketListResponseOk(res);
}
export function adaptWuyouWantBuyList(res, { pageNum = 1, pageSize = 20 } = {}) {
    if (!wuyouWantBuyListResponseOk(res))
        return { code: 0, data: { list: [] } };
    const rows = Array.isArray(res.rows) ? res.rows : [];
    const total = Number(res.total);
    const loaded = (Number(pageNum) - 1) * Number(pageSize) + rows.length;
    const hasNext = Number.isFinite(total) && total > 0 ? loaded < total : rows.length >= Number(pageSize);
    return { code: 1, data: { list: rows, hashNext: hasNext ? 1 : 0, hasNext: hasNext ? 1 : 0 } };
}
/** 求购列表 → OnSaleList 行形态（无挂单 id，仅展示） */
export function adaptWuyouWantBuyListToOnSaleLst(res, { pageNum = 1, pageSize = 20 } = {}) {
    const base = adaptWuyouWantBuyList(res, { pageNum, pageSize });
    if (base.code !== 1 || !base.data || !Array.isArray(base.data.list))
        return base;
    const list = base.data.list.map((r) => {
        const row = r && typeof r === "object" ? r : {};
        const price = row.wantBuyPrice ?? row.wantBuyMinPrice ?? row.price ?? row.amount ?? row.buyPrice ?? row.maxPrice;
        const title = String(row.productName ?? row.name ?? row.nftName ?? row.title ?? "").trim() || "\u6C42\u8D2d";
        const qty = row.num ?? row.count ?? row.quantity ?? row.buyNum ?? "";
        const buyC = row.buyCount;
        const wantC = row.wantBuyCount;
        const hasProgress = (buyC != null && String(buyC).trim() !== "") || (wantC != null && String(wantC).trim() !== "");
        const sub = hasProgress
            ? `\u5DF2\u8D2d/\u6C42\u8D2d\uFF1A${buyC != null && buyC !== "" ? buyC : 0}/${wantC != null && wantC !== "" ? wantC : 0}`
            : qty !== "" && qty != null ? `\u6C42\u8D2d ${qty} \u4EF6` : "\u6C42\u8D2d";
        return {
            ...row,
            wuyouWantBuyOnly: true,
            wuyouMarketListingId: "",
            wuyouRowTags: ["\u6C42\u8D2d"],
            wuyouNumberDisplay: sub,
            goodsNumber: sub,
            gId: "",
            gName: title,
            priceCny: price,
            price,
            resell: 0
        };
    });
    return { ...base, data: { ...base.data, list } };
}
export async function wuyouNoticeList(token, auth, { pageNum = 1, pageSize = 10, categoryId = WUYOU_NOTICE_CATEGORY_PLATFORM, tagId = "" } = {}) {
    const query = { pageNum, pageSize, categoryId: String(categoryId) };
    const t = String(tagId || "").trim();
    if (t)
        query.tagId = t;
    else
        query.tagId = "";
    return wuyouRequestWithAuth("GET", "/system/notice/app/getNoticeAndTag", token, auth, { query });
}
export async function wuyouNoticeDetail(token, auth, noticeId) {
    const id = encodeURIComponent(String(noticeId || "").trim());
    if (!id)
        return { code: 0 };
    return wuyouRequestWithAuth("GET", `/system/notice/app/${id}`, token, auth, { query: {} });
}
/**
 * 官方订单列表 GET /order/app/order/list（与 m 站「我的订单」一致）
 * - **status**：全部传空串 `status=`；待付款 `0`；已完成 `4`；已关闭 `-1`
 * - **批量付**：`status=0` 且 **`hasBatch=true`**（与仅「待付款」的 `status=0` 区分）
 * - `type` 为历史兼容；未传 `status` 时可附带 `type`
 */
export async function wuyouOrderList(token, auth, { pageNum = 1, pageSize = 20, type = "", status = void 0, hasBatch = void 0 } = {}) {
    const query = { pageNum, pageSize };
    const hasStatusKey = status !== void 0 && status !== null;
    const st = hasStatusKey ? String(status).trim() : "";
    if (hasStatusKey)
        query.status = st;
    if (hasBatch === true)
        query.hasBatch = true;
    const t = String(type || "").trim();
    if (t && (!hasStatusKey || st === "") && hasBatch !== true)
        query.type = t;
    return wuyouRequestWithAuth("GET", "/order/app/order/list", token, auth, { query });
}
export async function wuyouOrderInfo(token, auth, orderId, extraQuery = {}) {
    const id = encodeURIComponent(String(orderId || "").trim());
    if (!id)
        return { code: 0 };
    return wuyouRequestWithAuth("GET", `/order/app/order/info/${id}`, token, auth, { query: { ...extraQuery } });
}
/** 市场头部统计（参考 H5 / scan_013） */
export async function wuyouMarketGetHead(token, auth, productId) {
    const pid = encodeURIComponent(String(productId || "").trim());
    if (!pid)
        return { code: 0 };
    return wuyouRequestWithAuth("GET", `/market/app/market/getHead/${pid}`, token, auth, { query: {} });
}
/** 单笔寄售单详情 */
export async function wuyouMarketInfo(token, auth, marketId) {
    const mid = encodeURIComponent(String(marketId || "").trim());
    if (!mid)
        return { code: 0 };
    return wuyouRequestWithAuth("GET", `/market/app/market/info/${mid}`, token, auth, { query: {} });
}
/** 用户侧寄售列表（与 list 参数不同，见官方 H5） */
export async function wuyouMarketUserList(token, auth, query = {}) {
    return wuyouRequestWithAuth("GET", "/market/app/market/userList", token, auth, { query });
}
/** 某藏品成交/记录列表（与 scan_013 路径一致） */
export async function wuyouMarketRecordList(token, auth, query = {}) {
    return wuyouRequestWithAuth("GET", "/market/app/market/record/list", token, auth, { query });
}
/** 拉起第三方支付（汇付等），与 H5 同源 POST /pay/initiatePay */
export async function wuyouPayInitiatePay(token, auth, body = {}) {
    return wuyouRequestWithAuth("POST", "/pay/initiatePay", token, auth, { json: body });
}
/** 部分场景先 preInitiatePay */
export async function wuyouPayPreInitiatePay(token, auth, body = {}) {
    return wuyouRequestWithAuth("POST", "/pay/preInitiatePay", token, auth, { json: body });
}
export async function wuyouOrderCloseOrder(token, auth, orderId, body = {}) {
    const id = encodeURIComponent(String(orderId || "").trim());
    if (!id)
        return { code: 0 };
    return wuyouRequestWithAuth("POST", `/order/app/order/closeOrder/${id}`, token, auth, { json: body || {} });
}
export async function wuyouWantBuyList(token, auth, query = {}) {
    return wuyouRequestWithAuth("GET", "/market/app/want/buy/list", token, auth, { query });
}
/** 官方「我要求购」前置：GET /market/app/want/buy/preWantBuy/{productId}（scan_005 chunk-00f1） */
export async function wuyouWantBuyPreWantBuy(token, auth, productId, query = {}) {
    const pid = encodeURIComponent(String(productId || "").trim());
    if (!pid)
        return { code: 0, msg: "missing productId" };
    return wuyouRequestWithAuth("GET", `/market/app/want/buy/preWantBuy/${pid}`, token, auth, { query });
}
/** 官方发起求购：POST /market/app/want/buy（与官方 H5 同源） */
export async function wuyouWantBuyCreate(token, auth, body = {}) {
    return wuyouRequestWithAuth("POST", "/market/app/want/buy", token, auth, { json: body });
}
/** 官方：GET /market/app/want/buy/preBuyAuth/{productId}（与 buyAuth 配套，Token+Authorization 同 wuyouRequestWithAuth） */
export async function wuyouWantBuyPreBuyAuth(token, auth, productId, query = {}) {
    const pid = encodeURIComponent(String(productId || "").trim());
    if (!pid)
        return { code: 0, msg: "missing productId" };
    return wuyouRequestWithAuth("GET", `/market/app/want/buy/preBuyAuth/${pid}`, token, auth, { query });
}
/** 官方：阅读求购协议后上报，POST /market/app/want/buy/buyAuth/{productId}（scan_005 chunk-00f1，解决「未阅读协议无法发起求购」） */
export async function wuyouWantBuyBuyAuth(token, auth, productId, body = {}) {
    const pid = encodeURIComponent(String(productId || "").trim());
    if (!pid)
        return { code: 0, msg: "missing productId" };
    return wuyouRequestWithAuth("POST", `/market/app/want/buy/buyAuth/${pid}`, token, auth, { json: body });
}
/** 官方 H5：抢购/下单前 GET /product/app/product/changeRandomCode（与 scan_013 app.js 同源） */
export async function wuyouProductChangeRandomCode(token, auth, query = {}) {
    return wuyouRequestWithAuth("GET", "/product/app/product/changeRandomCode", token, auth, { query });
}
/** 从 changeRandomCode 响应中取出 randomCode（包体可能是 string 或对象） */
export function extractWuyouRandomCodeFromChange(res) {
    if (!res || typeof res !== "object")
        return "";
    if (Number(res.code) === 200 && res.data != null) {
        const d = res.data;
        if (typeof d === "string" && d.trim())
            return d.trim();
        if (d && typeof d === "object") {
            if (Object.prototype.hasOwnProperty.call(d, "randomCode"))
                return String(d.randomCode ?? "");
            const v = d.random ?? d.code;
            if (v != null && String(v).trim() !== "")
                return String(v).trim();
        }
    }
    const p = unwrapWuyouPayload(res);
    if (!p || typeof p !== "object")
        return "";
    if (typeof p === "string" && p.trim())
        return p.trim();
    const v2 = p.randomCode ?? p.random ?? (typeof p.data === "string" ? p.data : p.data?.randomCode);
    if (v2 != null && String(v2).trim() !== "")
        return String(v2).trim();
    return "";
}
export async function wuyouOrderPreOrder(token, auth, query = {}) {
    return wuyouRequestWithAuth("GET", "/order/app/order/preOrder", token, auth, { query });
}
export async function wuyouOrderSubmit(token, auth, body = {}) {
    const obj = body && typeof body === "object" ? { ...body } : {};
    return wuyouRequestWithAuth("POST", "/order/app/order/submit", token, auth, { json: mergeWuyouSignQuery(obj) });
}
export async function wuyouOrderBatchSubmit(token, auth, body = {}) {
    const obj = body && typeof body === "object" ? { ...body } : {};
    return wuyouRequestWithAuth("POST", "/order/app/order/batchSubmit", token, auth, { json: mergeWuyouSignQuery(obj) });
}
/**
 * 官方寄售/市场「自动批量」抢购：POST batchSubmit（与 lll.py / 官方 H5 一致）
 * body 含 hasAuto、maxPrice（限价字符串）、count、productId、payMethod、captcha 占位等；sign 由 mergeWuyouSignQuery 合并。
 */
export async function wuyouMarketAutoBatchBuy(token, auth, { productId, maxPriceYuan, count, payMethod = 41 } = {}) {
    const pid = String(productId || "").trim();
    const n = Math.min(9999, Math.max(1, Math.floor(Number(count) || 1)));
    const maxP = maxPriceYuan != null && String(maxPriceYuan).trim() !== "" ? String(maxPriceYuan).trim() : "";
    if (!pid || !maxP)
        return { code: 0, msg: "missing productId or maxPrice" };
    return wuyouOrderBatchSubmit(token, auth, {
        captchaRandstr: "",
        captchaTicket: "",
        count: n,
        hasAuto: true,
        maxPrice: maxP,
        payMethod: String(payMethod),
        productId: pid
    });
}
export function wuyouBatchSubmitResponseOk(res) {
    if (!res || typeof res !== "object")
        return false;
    return Number(res.code) === 200;
}
/** 发起寄售（body 含 sign 等，由调用方/H5 逻辑填充） */
export async function wuyouMarketResellCreate(token, auth, body = {}) {
    return wuyouRequestWithAuth("POST", "/market/app/market", token, auth, { json: body });
}
/** 取消寄售 listingId 为市场单 id */
export async function wuyouMarketResellCancel(token, auth, listingId, body = {}) {
    const id = encodeURIComponent(String(listingId || "").trim());
    if (!id)
        return { code: 0 };
    return wuyouRequestWithAuth("POST", `/market/app/market/${id}`, token, auth, { json: body });
}
export async function wuyouWantBuySellNft(token, auth, body = {}) {
    const obj = body && typeof body === "object" ? { ...body } : {};
    return wuyouRequestWithAuth("POST", "/market/app/want/buy/sellNft", token, auth, { json: mergeWuyouSignQuery(obj) });
}
export async function wuyouTransferRecordPreIncrease(token, auth, id, extraQuery = {}) {
    const enc = encodeURIComponent(String(id || "").trim());
    if (!enc)
        return { code: 0 };
    return wuyouRequestWithAuth("GET", `/product/app/transfer/record/preIncrease/${enc}`, token, auth, { query: extraQuery });
}
export async function wuyouComposeConsumeInfo(token, auth, ruleSetId) {
    const rid = encodeURIComponent(String(ruleSetId || "").trim());
    if (!rid)
        return { code: 0 };
    return wuyouRequestWithAuth("GET", `/activity/app/new/compose/append/consume/info/${rid}`, token, auth, { query: {} });
}
export async function wuyouComposePrepareToCompose(token, auth, body = {}) {
    return wuyouRequestWithAuth("POST", "/activity/app/new/compose/prepareToCompose", token, auth, { json: body });
}
export function adaptWuyouMarketListToNftWeb(res, { pageNum, pageSize }) {
    if (!wuyouMarketListResponseOk(res))
        return { code: 0, data: { list: [] } };
    const rows = Array.isArray(res.rows) ? res.rows : [];
    const list = rows.map((r) => {
        const productId = r.productId ?? r.product_id ?? r.albumId;
        const albumKey = productId != null && String(productId).trim() !== "" ? String(productId).trim() : String(r.id ?? "").trim();
        const name = r.name ?? r.productName ?? r.title ?? "";
        const thumb = r.image ?? r.img ?? r.coverUrl ?? r.pic ?? r.thumb ?? r.cover ?? r.productImg ?? "";
        const listingCount = r.count;
        const listingN = Number(listingCount);
        const listingDefined = listingCount != null && listingCount !== "" && Number.isFinite(listingN);
        const circulation = r.memberProductCount ?? r.marketCount ?? r.circulation ?? r.circulationNum ?? r.issuedCount ?? r.soldCount;
        let onSell = 1;
        if (listingDefined && listingN === 0)
            onSell = 0;
        else if (r.onSell != null && r.onSell !== "")
            onSell = Number(r.onSell);
        else if (r.hasMarket === false || r.hasMarket === 0 || r.hasMarket === "0")
            onSell = 0;
        else if ((productId == null || String(productId).trim() === "") && Number(r.status) === 0)
            onSell = 0;
        return {
            ...r,
            albumId: albumKey,
            productId: productId != null && String(productId).trim() !== "" ? String(productId).trim() : String(r.id ?? "").trim(),
            albumName: name,
            thumbPic: thumb,
            price: r.price,
            priceCny: r.price,
            soldNum: circulation,
            listingCount: listingCount,
            issueNum: r.issueNum ?? r.issueCount ?? r.issuedCount ?? r.limitNum,
            onSaleNum: r.onSaleNum ?? r.sellingCount,
            isFocus: Number(r.hasFollow) === 1 || r.isFocus === 1 ? 1 : 0,
            onSell
        };
    });
    const total = Number(res.total);
    const loaded = (Number(pageNum) - 1) * Number(pageSize) + rows.length;
    const hasNext = Number.isFinite(total) && total > 0 ? loaded < total : rows.length >= Number(pageSize);
    return { code: 1, data: { list, hashNext: hasNext ? 1 : 0, hasNext: hasNext ? 1 : 0 } };
}
export function wuyouParseTimeToMs(ctRaw) {
    if (ctRaw == null || ctRaw === "")
        return null;
    if (typeof ctRaw === "number" && Number.isFinite(ctRaw)) {
        if (ctRaw > 1e15)
            return Math.floor(ctRaw);
        if (ctRaw > 1e12)
            return Math.floor(ctRaw);
        if (ctRaw > 1e9)
            return Math.floor(ctRaw * 1e3);
        if (ctRaw > 0)
            return Math.floor(ctRaw * 1e3);
    }
    const s = String(ctRaw).trim();
    const n = Number(s);
    if (s !== "" && Number.isFinite(n) && !Number.isNaN(n)) {
        if (n > 1e15)
            return Math.floor(n);
        if (n > 1e12)
            return Math.floor(n);
        if (n > 1e9)
            return Math.floor(n * 1e3);
        if (n > 0)
            return Math.floor(n * 1e3);
    }
    const t = Date.parse(s.replace(/-/g, "/"));
    return Number.isNaN(t) ? null : t;
}
export function adaptWuyouOrderListToLst(res, { pageNum, pageSize }) {
    if (!wuyouOrderListResponseOk(res))
        return { code: 0, data: { list: [] } };
    const rows = Array.isArray(res.rows) ? res.rows : [];
    const list = rows.map((r) => {
        const oid = r.id ?? r.orderId ?? r.orderSn ?? r.orderNo;
        const ctRaw = r.createTime ?? r.payTime ?? r.orderTime ?? r.createDate;
        let createMs = wuyouParseTimeToMs(ctRaw);
        if (createMs == null)
            createMs = Date.now();
        const amt = r.payAmount ?? r.price ?? r.amount ?? r.orderAmount;
        const rawSt = Number(r.status ?? r.orderStatus ?? 2);
        const paySt = r.payStatus != null ? Number(r.payStatus) : NaN;
        let status = rawSt;
        if (rawSt === 0 || paySt === 0)
            status = 5;
        const numField = r.number ?? r.num ?? r.goodsNum ?? r.serialNum ?? r.productNumber;
        const totalField = r.totalNum ?? r.limitNum ?? r.issuedCount ?? r.issueNum;
        const thumbPic = r.productImage ?? r.productImg ?? r.image ?? r.picture ?? r.coverImg ?? r.goodsImg ?? r.thumb ?? r.nftImage ?? r.coverUrl ?? r.pic ?? "";
        const pidRaw = r.productId ?? r.albumId ?? r.goodsProductId ?? r.packProductId;
        const albumKey = pidRaw != null && String(pidRaw).trim() !== "" ? String(pidRaw).trim() : "";
        return {
            ...r,
            orderId: oid,
            goodsName: r.productName ?? r.goodsName ?? r.name ?? r.nftName ?? r.title ?? "—",
            goodsNum: numField,
            totalNum: totalField,
            createTime: createMs,
            status: Number.isFinite(status) ? status : 2,
            orderType: Number(r.orderType ?? r.type ?? 0),
            albumId: albumKey,
            goodsId: r.goodsId ?? r.gId ?? r.memberNftId,
            thumbPic,
            priceCny: amt,
            amount: amt
        };
    });
    const total = Number(res.total);
    const loaded = (Number(pageNum) - 1) * Number(pageSize) + rows.length;
    const hasNext = Number.isFinite(total) && total > 0 ? loaded < total : rows.length >= Number(pageSize);
    return { code: 1, data: { list, hashNext: hasNext ? 1 : 0, hasNext: hasNext ? 1 : 0 } };
}
/** 官方 member/nft/list 汇总行：持有总量在 num（见 api 探测），非「一行一件」时不要按行 +1 */
export function wuyouMemberNftRowOwnedQuantity(r) {
    if (!r || typeof r !== "object")
        return 1;
    const raw = r.num ?? r.count ?? r.holdNum ?? r.holdCount ?? r.quantity ?? r.totalNum;
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0)
        return Math.floor(n);
    return 1;
}
/** 在架寄售件数：优先数值字段，否则 hasMarket 仅作「至少 1 件在寄售」兜底 */
export function wuyouMemberNftRowOnSaleQuantity(r) {
    if (!r || typeof r !== "object")
        return 0;
    const raw = r.onSaleNum ?? r.sellingCount ?? r.sellingNum ?? r.marketSaleNum ?? r.consignmentCount ?? r.marketCount;
    const n = Number(raw);
    if (Number.isFinite(n) && n >= 0)
        return Math.floor(n);
    if (r.hasMarket === true || r.hasMarket === 1 || r.hasMarket === "1")
        return 1;
    return 0;
}
/**
 * 将 GET /market/app/market/userList 的按藏品寄售汇总（rows[].id 为 productId、count 为寄售件数）合并进仓库卡片 onSaleNum。
 * 与官方「我的寄售」列表同源接口。
 */
export function mergeWuyouMarketUserListOnSaleIntoWarehouseCards(cards, userListRes) {
    if (!Array.isArray(cards) || cards.length === 0)
        return;
    if (!wuyouMarketListResponseOk(userListRes))
        return;
    const rows = Array.isArray(userListRes.rows) ? userListRes.rows : [];
    const byProduct = new Map();
    for (const r of rows) {
        const pid = String(r.id ?? r.productId ?? "").trim();
        if (!pid)
            continue;
        const c = Number(r.count);
        if (Number.isFinite(c) && c >= 0)
            byProduct.set(pid, Math.floor(c));
    }
    for (const it of cards) {
        const k = String(it.albumId ?? "").trim();
        if (byProduct.has(k))
            it.onSaleNum = byProduct.get(k);
    }
}
/** 无忧订单列表按藏品 productId 分组（用于与官方「按类型汇总」一致的 UI） */
export function groupWuyouOrdersByAlbumId(orderItems) {
    const arr = Array.isArray(orderItems) ? orderItems : [];
    const m = new Map();
    for (const it of arr) {
        const key = String(it?.albumId ?? "").trim() || `__noid__${String(it?.orderId ?? "")}`;
        if (!m.has(key)) {
            m.set(key, {
                key,
                albumId: String(it?.albumId ?? "").trim(),
                name: String(it?.goodsName ?? "—").trim() || "—",
                thumbPic: it?.thumbPic ?? "",
                items: []
            });
        }
        m.get(key).items.push(it);
    }
    const blocks = [...m.values()];
    for (const b of blocks) {
        b.items.sort((a, b2) => (Number(b2.createTime) || 0) - (Number(a.createTime) || 0));
        const cover = b.items.map((x) => String(x.thumbPic || x.productImage || x.picture || x.image || "").trim()).find((s) => s.length > 0);
        if (cover)
            b.thumbPic = cover;
    }
    blocks.sort((a, b) => {
        const ta = a.items[0] ? Number(a.items[0].createTime) || 0 : 0;
        const tb = b.items[0] ? Number(b.items[0].createTime) || 0 : 0;
        return tb - ta;
    });
    return blocks;
}
export function aggregateWuyouMemberNftRows(rows) {
    const m = new Map();
    for (const r of rows) {
        const prod = String(r.productId ?? "").trim();
        const nid = String(r.nftId ?? "").trim();
        const key = prod || nid;
        if (!key)
            continue;
        const owned = wuyouMemberNftRowOwnedQuantity(r);
        const onSaleInc = wuyouMemberNftRowOnSaleQuantity(r);
        if (!m.has(key)) {
            m.set(key, {
                albumId: prod || nid,
                albumName: String(r.nftName ?? r.name ?? "—"),
                thumbPic: r.picture ?? r.image ?? "",
                authorName: String(r.authorName ?? r.creatorName ?? "").trim(),
                authorHeadImage: r.authorImage ?? r.authorHeadImage ?? r.creatorAvatar ?? "",
                totalNum: 0,
                onSaleNum: 0,
                wuyouNftId: nid || null,
                floorPrice: "",
                sellLimit: r.issueNum ?? r.limitNum ?? r.totalNum ?? r.sellLimit ?? ""
            });
        }
        const o = m.get(key);
        o.totalNum += owned;
        o.onSaleNum += onSaleInc;
        if (!o.wuyouNftId && r.nftId != null)
            o.wuyouNftId = String(r.nftId);
        const bp = r.buyPrice ?? r.price ?? r.sellPrice ?? r.floorPrice;
        if (bp != null && String(bp).trim() !== "") {
            const n = Number(bp);
            const cur = o.floorPrice === "" ? NaN : Number(o.floorPrice);
            if (o.floorPrice === "" || (Number.isFinite(n) && (!Number.isFinite(cur) || n < cur)))
                o.floorPrice = String(bp);
        }
        if (!o.authorName && r.authorName)
            o.authorName = String(r.authorName).trim();
        if (!o.authorHeadImage && (r.authorImage || r.authorHeadImage))
            o.authorHeadImage = r.authorImage ?? r.authorHeadImage;
        if ((o.sellLimit == null || o.sellLimit === "") && (r.issueNum ?? r.limitNum ?? r.sellLimit) != null)
            o.sellLimit = r.issueNum ?? r.limitNum ?? r.sellLimit;
    }
    return [...m.values()];
}
export function mapWuyouOrderDetailToLst(d) {
    if (!d || typeof d !== "object")
        return null;
    const ctRaw = d.createTime ?? d.payTime ?? d.orderTime ?? d.createDate;
    let time = wuyouParseTimeToMs(ctRaw);
    if (time == null)
        time = Date.now();
    const amt = d.payAmount ?? d.price ?? d.amount ?? d.orderAmount ?? d.shouldPayPrice ?? d.totalPrice ?? d.productPrice;
    const rawSt = Number(d.status ?? d.orderStatus ?? NaN);
    const paySt = d.payStatus != null ? Number(d.payStatus) : NaN;
    let st = rawSt;
    if (rawSt === 0 || paySt === 0)
        st = 5;
    if (!Number.isFinite(st))
        st = 2;
    const thumbPic = d.productImage ?? d.image ?? d.picture ?? d.coverImg ?? d.productImg ?? d.goodsImg ?? d.thumb ?? d.nftImage ?? d.coverUrl ?? d.pic ?? "";
    const { status: _ignoreApiStatus, ...restD } = d;
    return {
        ...restD,
        orderId: String(d.id ?? d.orderSn ?? d.orderId ?? ""),
        goodsName: String(d.productName ?? d.goodsName ?? d.nftName ?? d.name ?? "\u2014").trim() || "\u2014",
        goodsNum: d.num ?? d.goodsNum ?? d.number ?? d.serialNum ?? d.productNumber ?? d.payNum,
        totalNum: d.totalNum ?? d.totalCount ?? d.limitNum ?? d.issuedCount,
        albumId: d.productId ?? d.albumId,
        goodsId: d.goodsId ?? d.memberNftId ?? d.gId,
        thumbPic,
        amount: amt,
        originalAmount: amt,
        diamondType: 0,
        status: st,
        orderType: Number(d.orderType ?? d.type ?? 0),
        time,
        supportPanicBuy: 0,
        wuyouPayUrl: d.payUrl ?? d.cashierUrl ?? d.h5Url ?? d.url ?? "",
    };
}
/** 从 initiatePay / preInitiatePay 响应中提取收银台 URL */
export function extractWuyouPayRedirectUrl(res) {
    if (!res || typeof res !== "object")
        return "";
    const tryObj = (o) => {
        if (!o || typeof o !== "object")
            return "";
        const keys = ["payUrl", "cashierUrl", "h5Url", "url", "link", "mwebUrl", "wapUrl", "jumpUrl", "payLink"];
        for (const k of keys) {
            const v = o[k];
            if (v != null && String(v).trim() !== "" && /^https?:\/\//i.test(String(v).trim()))
                return String(v).trim();
        }
        return "";
    };
    if (Number(res.code) === 200 && res.data)
        return tryObj(res.data);
    return tryObj(res) || tryObj(unwrapWuyouPayload(res));
}
export function wuyouNoticeDetailResponseOk(res) {
    if (!res || typeof res !== "object")
        return false;
    if (Number(res.code) === 200)
        return true;
    const p = unwrapWuyouPayload(res);
    return !!(p && (p.noticeContent != null || p.noticeTitle != null));
}
/**
 * 二级市场购买：与甲方 `platform_wuyou_api_reference.json` 及官方 H5（scan_013）一致——
 * 先 GET changeRandomCode 取 randomCode，再 GET preOrder（含 productId、count、type、randomCode 及挂单 id 等），最后 POST submit（body 合并 sign_*，并补 payMethod）。
 * 与 LST/镜权不同：不得沿用 /nft-web 下单字段。
 */
function normalizeWuyouSecondarySubmitBody(body, { hasMarketListing = false, defaultPayMethod = 41 } = {}) {
    const out = { ...(body && typeof body === "object" ? body : {}) };
    if (out.payMethod == null || String(out.payMethod).trim() === "")
        out.payMethod = defaultPayMethod;
    if (hasMarketListing) {
        const c = Number(out.count);
        if (!Number.isFinite(c) || c > 1)
            out.count = 1;
    }
    return out;
}
async function wuyouTryFetchRandomCodeForBuy(token, auth, productId, marketListingId) {
    const pid = String(productId || "").trim();
    const mid = String(marketListingId || "").trim();
    /** 官方接口要求 query 中必须带 randomCode（可为空串），否则 500：Required request parameter 'randomCode' */
    const tries = [{ productId: pid, randomCode: "" }];
    if (mid) {
        tries.push({ productId: pid, id: mid, randomCode: "" }, { productId: pid, marketId: mid, randomCode: "" });
    }
    for (const q of tries) {
        const raw = await wuyouProductChangeRandomCode(token, auth, q);
        if (raw && Number(raw.code) === 200 && raw.data != null && typeof raw.data === "object"
            && Object.prototype.hasOwnProperty.call(raw.data, "randomCode"))
            return String(raw.data.randomCode ?? "");
        const rc = extractWuyouRandomCodeFromChange(raw);
        if (rc !== "")
            return rc;
    }
    return "";
}
export async function wuyouBuyNftSecondary(token, auth, { productId, memberNftId, marketListingId }) {
    const pid = String(productId || "").trim();
    const nid = String(memberNftId || "").trim();
    if (!pid || !nid)
        return { code: 0, msg: "missing productId or member nft id" };
    const mid = String(marketListingId || "").trim();
    const randomCode = await wuyouTryFetchRandomCodeForBuy(token, auth, pid, mid);
    /** 须带 randomCode 查询键（可为 ""）；仅 omit 作为兜底 */
    const randomTries = [...new Set([randomCode, null])];
    /** 寄售常见 type=2；与 undefined 等组合由服务端择优 */
    const typeTries = [2, void 0, 1, 0, 3];
    const attempts = [];
    const pushQ = (q) => {
        const key = JSON.stringify(q);
        if (!attempts.some((x) => JSON.stringify(x) === key))
            attempts.push(q);
    };
    for (const rv of randomTries) {
        for (const typ of typeTries) {
            const base = { productId: pid, count: 1 };
            if (typ !== void 0 && typ !== null)
                base.type = typ;
            if (rv !== null && rv !== undefined)
                base.randomCode = rv;
            if (mid) {
                pushQ({ ...base, id: mid });
                pushQ({ ...base, marketId: mid });
                pushQ({ ...base, marketListingId: mid });
            }
            pushQ({ ...base, id: nid });
            pushQ({ ...base, nftId: nid });
            pushQ({ ...base, memberNftId: nid });
        }
    }
    let last = null;
    for (const q of attempts) {
        last = await wuyouOrderPreOrder(token, auth, q);
        if (wuyouPreOrderResponseOk(last))
            break;
    }
    if (!wuyouPreOrderResponseOk(last))
        return { code: 0, msg: last?.msg || last?.message || "preOrder failed", raw: last };
    const body = unwrapWuyouPreOrderData(last);
    if (!body || typeof body !== "object")
        return { code: 0, msg: "preOrder empty", raw: last };
    const subBody = normalizeWuyouSecondarySubmitBody(body, { hasMarketListing: Boolean(mid) });
    const sub = await wuyouOrderSubmit(token, auth, subBody);
    if (!wuyouOrderSubmitResponseOk(sub))
        return { code: 0, msg: sub?.msg || sub?.message || "submit failed", raw: sub };
    const oid = sub?.data?.orderId ?? sub?.orderId ?? sub?.data?.id ?? sub?.id;
    if (oid == null || String(oid).trim() === "")
        return { code: 0, msg: "no order id", raw: sub };
    return { code: 1, data: { orderId: String(oid) } };
}
